import {
  CameraOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  MobileOutlined,
  PlusOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PERMISSIONS } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation, useDownload, usePaged } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { openBatchPrint } from '@/components/BatchPrintPage';
import { createdAtColumn } from '@/components/createdAtColumn';
import { StickyTable } from '@/components/StickyTable';
import { TableCard } from '@/components/TableCard';
import { fmt } from '@/lib/labels';

import { ViolationFormModal } from './violations/ViolationFormModal';
import { ViolationPhotoPreview, useViolationPhotoPreview } from './violations/ViolationPhotoPreview';

const { RangePicker } = DatePicker;

interface ViolationRow {
  id: number;
  occurredAt: string;
  fineAmount: string | null;
  description: string | null;
  photoKey: string | null;
  createdAt: string;
  driver: { id: number; lastName: string; firstName: string; middleName: string | null; personnelNumber: string };
  vehicle: { id: number; garageNumber: string; plateNumber: string | null } | null;
  type: { id: number; name: string };
  issuedByUser: { id: number; fullName: string } | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

interface Standings {
  limit: number;
  periodMonths: number;
  drivers: Array<{ driverId: number; count: number; blocked: boolean }>;
}

/**
 * Журнал нарушений — рабочее место службы безопасности дорог аэропорта.
 *
 * Отдельное от карточки водителя меню, потому что доступ к нему выдаётся
 * отдельным правом (violation.manage): сотрудник БД не должен получать
 * заодно доступ к остальной карточке водителя, а начальник автослужбы —
 * не должен оформлять нарушения от чужого имени.
 */
export function ViolationsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ViolationRow | null>(null);
  const [cancelForm] = Form.useForm<{ reason: string }>();
  const photoPreview = useViolationPhotoPreview();
  const download = useDownload();

  const canManage = can(PERMISSIONS.VIOLATION_MANAGE);

  const filters = { dateFrom: dateRange?.[0], dateTo: dateRange?.[1] };

  const query = usePaged<ViolationRow>(['violations'], '/violations', {
    page,
    pageSize,
    ...filters,
  });

  // Сколько действующих нарушений у каждого водителя за скользящий период —
  // по этим цифрам диспетчер не сможет выдать лист отстранённому.
  const standings = useQuery({
    queryKey: ['violation-standings'],
    queryFn: async () => (await api.get<Standings>('/violations/standings')).data,
  });
  const standingOf = new Map((standings.data?.drivers ?? []).map((d) => [d.driverId, d]));
  const blockedCount = (standings.data?.drivers ?? []).filter((d) => d.blocked).length;

  const cancel = useApiMutation(
    async ({ id, reason }: { id: number; reason: string }) =>
      (await api.post(`/violations/${id}/cancel`, { reason })).data,
    {
      successMessage: t('Нарушение погашено'),
      invalidate: [['violations'], ['violation-standings'], ['driver-eligibility']],
    },
  );

  const remove = useApiMutation(
    async (id: number) => (await api.delete(`/violations/${id}`)).data,
    { successMessage: t('Запись удалена'), invalidate: [['violations'], ['violation-standings']] },
  );

  if (!can(PERMISSIONS.VIOLATION_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр нарушений')}</Typography.Text>;
  }

  return (
    <TableCard
      title={
        <Space wrap size="small">
          <span>{t('Нарушения')}</span>
          {standings.data && (
            <Tooltip
              title={t(
                'Водитель с таким числом действующих нарушений за период не получает путевые листы, пока нарушение не погашено',
              )}
            >
              <Badge
                status="processing"
                text={`${t('лимит')}: ${standings.data.limit} ${t('за')} ${standings.data.periodMonths} ${t('мес.')}`}
              />
            </Tooltip>
          )}
          {blockedCount > 0 && (
            <Badge status="error" text={`${t('отстранено')}: ${blockedCount}`} />
          )}
        </Space>
      }
      extra={
        <Space wrap>
          <RangePicker
            format="DD.MM.YYYY"
            onChange={(_, formatted) => {
              setDateRange(formatted[0] && formatted[1] ? [formatted[0], formatted[1]] : null);
              setPage(1);
            }}
          />
          {canManage && (
            <>
              {/*
                Открывает упрощённый экран (FieldViolationPage) в новой вкладке —
                тот же сценарий, но без сайдбара и таблиц, под палец на телефоне
                или планшете сотрудника БД в поле. Журнал в текущей вкладке при
                этом остаётся открытым.
              */}
              <Tooltip title={t('Открыть мобильный экран для оформления в поле')}>
                <Link to="/field/violations" target="_blank" rel="noopener">
                  <Button icon={<MobileOutlined />}>{t('Мобильный режим')}</Button>
                </Link>
              </Tooltip>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
                {t('Оформить нарушение')}
              </Button>
            </>
          )}
        </Space>
      }
    >
      <StickyTable<ViolationRow>
        rowKey="id"
        exportSource={{ url: '/violations', params: filters }}
        rowNumbers
        // Для отмеченных записей — не только таблица, но и сами формы.
        reports={(rows) => [
          {
            key: 'forms',
            label: t('Печатные формы'),
            formats: [
              {
                type: 'print',
                title: t('Печать'),
                run: () =>
                  openBatchPrint(
                    '/violations/print',
                    rows.map((row) => row.id),
                    t('Нарушения'),
                  ),
              },
            ],
          },
        ]}
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${t('Всего:')} ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
        columns={[
          {
            title: t('Дата'),
            dataIndex: 'occurredAt',
            width: 150,
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
          },
          {
            title: t('Водитель'),
            render: (_: unknown, row: ViolationRow) => {
              const standing = standingOf.get(row.driver.id);
              const limit = standings.data?.limit;
              return (
                <Space size={6} wrap>
                  <span>
                    {`${row.driver.lastName} ${row.driver.firstName} ${row.driver.middleName ?? ''}`.trim()}
                  </span>
                  {standing && limit !== undefined && (
                    <Tooltip
                      title={
                        standing.blocked
                          ? t('Лимит нарушений исчерпан — путевые листы не выдаются')
                          : t('Действующих нарушений за период')
                      }
                    >
                      <Tag color={standing.blocked ? 'error' : standing.count === limit - 1 ? 'warning' : 'default'} bordered={false}>
                        {standing.count}/{limit}
                      </Tag>
                    </Tooltip>
                  )}
                </Space>
              );
            },
          },
          {
            title: t('Статус'),
            width: 130,
            render: (_: unknown, row: ViolationRow) =>
              row.cancelledAt ? (
                <Tooltip
                  title={`${dayjs(row.cancelledAt).format('DD.MM.YYYY HH:mm')}${row.cancelReason ? ` · ${row.cancelReason}` : ''}`}
                >
                  <Tag bordered={false} color="success">{t('Погашено')}</Tag>
                </Tooltip>
              ) : (
                <Tag bordered={false} color="volcano">{t('Действует')}</Tag>
              ),
          },
          {
            title: t('Техника'),
            width: 160,
            render: (_: unknown, row: ViolationRow) =>
              row.vehicle
                ? `${row.vehicle.garageNumber}${row.vehicle.plateNumber ? ` · ${row.vehicle.plateNumber}` : ''}`
                : '—',
          },
          { title: t('Вид нарушения'), width: 220, render: (_: unknown, row: ViolationRow) => row.type.name },
          {
            title: t('Штраф'),
            dataIndex: 'fineAmount',
            width: 120,
            align: 'right',
            render: (value: string | null) => (value ? fmt(value) : '—'),
          },
          {
            title: t('Оформил'),
            width: 180,
            render: (_: unknown, row: ViolationRow) => row.issuedByUser?.fullName ?? '—',
          },
          createdAtColumn<ViolationRow>(t),
          {
            title: '',
            width: 190,
            render: (_: unknown, row: ViolationRow) => (
              <Space size={0}>
                {canManage && !row.cancelledAt && (
                  <Tooltip title={t('Погасить')}>
                    <Button
                      type="text"
                      icon={<CheckCircleOutlined />}
                      onClick={() => {
                        cancelForm.resetFields();
                        setCancelTarget(row);
                      }}
                    />
                  </Tooltip>
                )}
                {row.photoKey && (
                  <Tooltip
                    title={`${t('Показать фото')} · ${t('Загружено')} ${dayjs(row.createdAt).format('DD.MM.YYYY HH:mm')}`}
                  >
                    <Button
                      type="text"
                      icon={<CameraOutlined />}
                      onClick={() => photoPreview.open(row.id)}
                    />
                  </Tooltip>
                )}
                <Tooltip title={t('Печать')}>
                  <Button
                    type="text"
                    icon={<PrinterOutlined />}
                    onClick={() =>
                      window.open(`/violations/${row.id}/print`, '_blank', 'noopener')
                    }
                  />
                </Tooltip>
                <Tooltip title="Excel">
                  <Button
                    type="text"
                    icon={<FileExcelOutlined />}
                    onClick={() =>
                      download(`/violations/${row.id}/export.csv`, {}, `narushenie-${row.id}.csv`)
                    }
                  />
                </Tooltip>
                {canManage && (
                  <Popconfirm
                    title={t('Удалить запись о нарушении?')}
                    okText={t('Удалить')}
                    cancelText={t('Отмена')}
                    onConfirm={() => remove.mutate(row.id)}
                  >
                    <Tooltip title={t('Удалить')}>
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
        expandable={{
          rowExpandable: (row) => Boolean(row.description),
          expandedRowRender: (row) => (
            <Typography.Text type="secondary">{row.description}</Typography.Text>
          ),
        }}
      />

      <ViolationFormModal open={formOpen} onClose={() => setFormOpen(false)} />

      <Modal
        open={cancelTarget !== null}
        title={t('Погашение нарушения')}
        okText={t('Погасить')}
        cancelText={t('Отмена')}
        confirmLoading={cancel.isPending}
        onCancel={() => setCancelTarget(null)}
        onOk={() => {
          void cancelForm.validateFields().then(({ reason }) => {
            if (!cancelTarget) return;
            cancel.mutate(
              { id: cancelTarget.id, reason },
              { onSuccess: () => setCancelTarget(null) },
            );
          });
        }}
      >
        <Typography.Paragraph type="secondary">
          {t(
            'Погашенное нарушение остаётся в журнале, но не учитывается в лимите: если водитель был отстранён, он снова сможет получать путевые листы.',
          )}
        </Typography.Paragraph>
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="reason"
            label={t('Основание')}
            rules={[
              { required: true, message: t('Укажите основание') },
              { min: 3, message: t('Слишком коротко') },
            ]}
          >
            <Input.TextArea rows={2} maxLength={400} placeholder={t('Например: штраф оплачен, квитанция №…')} />
          </Form.Item>
        </Form>
      </Modal>

      {photoPreview.target !== null && (
        <ViolationPhotoPreview
          violationId={photoPreview.target}
          open
          onClose={photoPreview.close}
        />
      )}
    </TableCard>
  );
}
